import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { Layout, Modal, Select } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store";
import { ReleaseNotesProps } from "../types";
import { fetchReleaseNotes } from "../slices/appInfoSlice";

const ReleaseNotes = ({ open, onCancel, onHighlight }: ReleaseNotesProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.user.userDetails);
  const isAppLoading = useSelector((state: RootState) => state.user.loading);
  const releaseNotes = useSelector((state: RootState) => state.appInfo.releaseNotes);
  const [tags, setTags] = useState<string[]>([]);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [selectedTag, setSelectedTag] = useState<string>("");
  const [shouldHighlight, setShouldHighlight] = useState<boolean>(false);
  const localStorageTagKey = "latest_release_version";
 
  useEffect(() => {
    if(user){
    dispatch(fetchReleaseNotes());
    }
  }, [user]);

  useEffect(() => {
    if (releaseNotes && Array.isArray(releaseNotes)) {
        const releaseNotesCopy = [...releaseNotes];
        const sortedData = releaseNotesCopy.sort((a: { tag: string; }, b: { tag: string; }) =>
            compareVersions(b.tag, a.tag)
          );
        const tagsMap = sortedData.reduce(
        (
            acc: Record<string, string>,
            item: { tag: string; note: string }
        ) => {
            acc[item.tag] = item.note;
            return acc;
        }, {});

        const latestTag = sortedData[0]?.tag || "";
        setTags(Object.keys(tagsMap));
        setNotes(tagsMap);
        setSelectedTag(Object.keys(tagsMap)[0] || "");

        const storedVersion = localStorage.getItem(localStorageTagKey);
        if (storedVersion !== latestTag) {
          setShouldHighlight(true);
        }

        localStorage.setItem(localStorageTagKey, latestTag);
    }
  }, [releaseNotes]);

  // Separate useEffect to trigger highlighting only when loading is complete
  useEffect(() => {
    if (!isAppLoading && shouldHighlight) {
      onHighlight();
      setShouldHighlight(false);
    }
  }, [isAppLoading, shouldHighlight, onHighlight]);

  const compareVersions = (a: string, b: string): number => {
    /**
     * The sorting logic below sorts versions with any number of parts (digits), for eg:
     * 2.4.3.1, 2.4.3, 2.4.2, 2.4.3.2 should be sorted as: 2.4.3.2, 2.4.3.1, 2.4.3, 2.4.2
     */
    const parseVersion = (v: string) => v.split(".").map(Number);
    const aParts = parseVersion(a);
    const bParts = parseVersion(b);
    const maxLength = Math.max(aParts.length, bParts.length);
    
    for (let i = 0; i < maxLength; i++) {
      const aPart = aParts[i] || 0;
      const bPart = bParts[i] || 0;
      if (aPart !== bPart) {
        return aPart - bPart;
      }
    }
    return 0;
  };

  const handleTagChange = (value: string) => {
    setSelectedTag(value);
  };

  return (
    <Modal
      title="Release Notes"
      open={open}
      style={{ top: 20, height: "calc(100vh - 20px)" }}
      width={1024}
      onCancel={onCancel}
      maskClosable={true}
      footer={[
        <button
          className="bg-accent text-white px-4 py-2 rounded"
          onClick={onCancel}
        >
          Close
        </button>,
      ]}
    >
      <Layout className="bg-transparent">
        <div className="flex flex-col">
          <label>Version</label>
          <Select
            value={selectedTag}
            onChange={handleTagChange}
            placeholder="Select a version"
            className="w-[200px]"
          >
            {tags.map((tag) => (
              <Select.Option key={tag} value={tag}>
                {tag}
              </Select.Option>
            ))}
          </Select>
        </div>
        <div className="mt-3 h-[50vh] overflow-y-auto">
          {selectedTag && (
            <ReactMarkdown
              rehypePlugins={[rehypeRaw]}
              remarkPlugins={[remarkGfm]}
              className="user-guide-content-style"
            >
              {notes[selectedTag] || ""}
            </ReactMarkdown>
          )}
        </div>
      </Layout>
    </Modal>
  );
};

export default ReleaseNotes;
